<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  apiReferenceDate,
  maskReferenceDate,
  referenceDateComplete,
} from './postSaleDate';

const props = defineProps({
  caseData: { type: Object, required: true },
  isLoading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const form = reactive({ cancellationDate: '', reason: '' });
const attempted = ref(false);

const address = computed(() => {
  const value = props.caseData.transaction?.address || {};
  const street = [value.logradouro || value.endereco, value.numero]
    .filter(Boolean)
    .join(', ');
  const unit = [value.unidade, value.complemento].filter(Boolean).join(', ');
  const location = [value.bairro, value.cidade].filter(Boolean).join(' - ');
  return [street, unit, location].filter(Boolean).join(', ');
});

const manager = computed(
  () =>
    props.caseData.manager?.name ||
    t('POST_SALE.DETAIL.CANCEL_MANAGER_FALLBACK')
);
const validDate = computed(() => referenceDateComplete(form.cancellationDate));
const validReason = computed(() => Boolean(form.reason.trim()));
const valid = computed(() => validDate.value && validReason.value);

watch(
  () => props.show,
  visible => {
    if (!visible) return;
    form.cancellationDate = '';
    form.reason = '';
    attempted.value = false;
  }
);

const close = () => emit('close');
const updateDate = event => {
  form.cancellationDate = maskReferenceDate(event.target.value);
};
const confirm = () => {
  attempted.value = true;
  if (!valid.value) return;

  emit('confirm', {
    cancellationDate: apiReferenceDate(form.cancellationDate),
    reason: form.reason.trim(),
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('POST_SALE.DETAIL.CANCEL_TITLE')"
    @close="close"
  >
    <p class="text-sm leading-6 text-lais-text-body">
      {{
        t('POST_SALE.DETAIL.CANCEL_CONTEXT', {
          address,
          manager,
        })
      }}
    </p>
    <div class="mt-5 grid gap-4">
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.DETAIL.CANCEL_DATE_LABEL') }}
        <input
          :value="form.cancellationDate"
          type="text"
          maxlength="10"
          :placeholder="t('POST_SALE.DETAIL.CANCEL_DATE_PLACEHOLDER')"
          class="min-h-10 rounded-lais border px-3 font-normal"
          :class="
            attempted && !validDate ? 'border-red-500' : 'border-lais-border'
          "
          @input="updateDate"
        />
        <span
          v-if="attempted && !validDate"
          class="text-xs font-normal text-red-600"
        >
          {{ t('POST_SALE.FORM.VALIDATION.INVALID_DATE') }}
        </span>
      </label>
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.DETAIL.CANCEL_REASON_LABEL') }}
        <textarea
          v-model="form.reason"
          rows="3"
          :placeholder="t('POST_SALE.DETAIL.CANCEL_REASON_PLACEHOLDER')"
          class="rounded-lais border border-lais-border p-3 font-normal"
          :class="attempted && !validReason ? 'border-red-500' : ''"
        />
        <span
          v-if="attempted && !validReason"
          class="text-xs font-normal text-red-600"
        >
          {{ t('POST_SALE.FORM.VALIDATION.REQUIRED') }}
        </span>
      </label>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="close">
          {{ t('POST_SALE.ACTIONS.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton :is-loading="isLoading" @click="confirm">
          {{ t('POST_SALE.ACTIONS.CANCEL_SALE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
